// React Dependencies
import React, { useRef, useState, useMemo } from 'react';

// External Dependencies
import MaterialTable from 'material-table';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { Delete as DeleteIcon, Edit as EditIcon } from '@material-ui/icons';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  IconButton, Button,
  Select, MenuItem, makeStyles,
} from '@material-ui/core';

// Internal Dependencies
import ApiService from '../../../services.js/apiService';
import MText from '../../common/MText';
import Colours from '../../../styles/colours';
import { IsEmpty } from '../../../common/checks';
import { Status } from '../../../common/constants';
import {
  toggleModal as toggleModalAction,
  updateModalContent as updateModalAction,
} from '../../../actions/modal';
import AlbumCreateModal from './components/AlbumCreateModal';

const useStyles = makeStyles(() => ({
  rowButton: {
    textTransform: 'none',
    marginLeft:    '.3rem',
  },
}));

const Albums = ({ match, updateModalContent, toggleModal }) => {
  const { id: pageId } = match.params;

  const tableRef = useRef(null);
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  const fetchTableData = async (query) => {
    try {
      setLoading(true);

      const pageNo = query.page;
      const queryFilters = Object.fromEntries(
        query.filters.map(({ column, value }) => [
          column.field === 'id' ? 'albumId' : column.field,
          value,
        ]),
      );

      const result = await ApiService.getPageAlbums(pageId, {
        ...query,
        pageNo,
        ...queryFilters,
      });

      return { data: result.rows, page: pageNo, totalCount: result.totalCount };
    } catch (err) {
      toast.error(err.message);
      return { data: [], page: 0, totalCount: 0 };
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    updateModalContent({
      title:        'Are you sure?',
      content:      'All data related to this album will be deleted!',
      actionButton: {
        actionText: 'Yes, Delete',
        actionType: 'danger',
        action:     async () => {
          try {
            setLoading(true);

            await ApiService.deletePageAlbum(pageId, id);

            tableRef.current.onQueryChange();
            toast.success('Successfully deleted album');
          } catch (err) {
            toast.error(err.message);
          } finally {
            setLoading(false);
          }
        },
      },
      cancelText:       'No',
      closeAfterAction: true,
    });
    toggleModal();
  };

  const defaultColumns = useMemo(() => [
    {
      title:     'ID',
      filtering: true,
      editable:  'never',
      field:     'id',
      cellStyle: { width: '15%' },
    },
    {
      title:     'Name',
      filtering: true,
      editable:  'never',
      field:     'name',
      cellStyle: { width: '30%' },
      render:    (row) => (
        <Link target="_blank" to={`/admin/page/${pageId}/album/${row.id}`}>
          <MText size="13px" colour={Colours.Link} text={row.name} />
        </Link>
      ),
    },
    {
      title:           'Status',
      field:           'status',
      filtering:       true,
      editable:        'never',
      cellStyle:       { width: '15%' },
      filterComponent: (props) => (
        <Select
          className="w-100"
          // eslint-disable-next-line react/prop-types
          onChange={(e) => props.onFilterChanged(props.columnDef.tableData.id, e.target.value)}
        >
          <MenuItem value="">-</MenuItem>
          <MenuItem value={Status.Draft}>{Status.Draft}</MenuItem>
          <MenuItem value={Status.Live}>{Status.Live}</MenuItem>
          <MenuItem value={Status.Rejected}>{Status.Rejected}</MenuItem>
        </Select>
      ),
    },
    {
      title:     'POI',
      filtering: true,
      editable:  'never',
      field:     'poiName',
      cellStyle: { width: '30%' },
      render:    (row) => {
        if (IsEmpty(row.poiId)) {
          return '-';
        } else {
          return (
            <Link target="_blank" to={`/admin/poi/${row.poiId}`}>
              <MText size="13px" colour={Colours.Link} text={row.poiName} />
            </Link>
          );
        }
      },
    },
    {
      title:     'Actions',
      cellStyle: { width: '10%' },
      filtering: false,
      render:    (row) => (
        <div className="w-100 d-flex align-items-center justify-content-end">
          {[Status.Draft].includes(row.status) && (
            <IconButton
              onClick={() => handleDelete(row.id)}
              className={classes.rowButton}
            >
              <DeleteIcon htmlColor={Colours.Black} />
            </IconButton>
          )}
          <IconButton
            onClick={() => window.open(`/admin/page/${pageId}/album/${row.id}`, '_blank')}
            className={classes.rowButton}
          >
            <EditIcon htmlColor={Colours.Black} />
          </IconButton>
        </div>
      ),
    },
  ], []);

  return (
    <>
      <div className="container-fluid d-flex align-items-center mb-3">
        <MText bold size="24px" text="Albums" />
        <Button className="ml-4 px-3" onClick={() => setIsAdding(true)} variant="contained" color="primary">+ Add</Button>
      </div>

      <div className="container-fluid">
        <MaterialTable
          tableRef={tableRef}
          columns={defaultColumns}
          isLoading={loading}
          components={{ Toolbar: () => null }}
          options={{ filtering: true, search: false, debounceInterval: 500, emptyRowsWhenPaging: false }}
          data={fetchTableData}
        />
      </div>

      <AlbumCreateModal
        isOpen={isAdding}
        pageId={pageId}
        onClose={() => setIsAdding(false)}
      />
    </>
  );
};

Albums.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape(Object),
  }).isRequired,
  updateModalContent: PropTypes.func.isRequired,
  toggleModal:        PropTypes.func.isRequired,
};

export default connect(
  null,
  { toggleModal: toggleModalAction, updateModalContent: updateModalAction },
)(Albums);
